import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { useSession } from '@/lib/auth/session';
import { SettingsPanel } from '@/shared/components/SettingsPanel';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { displayNameOf } from '@/shared/components/accountIdentity';
import { clearPersonalDrafts } from '@/shared/store/personalDrafts';

import { ACCOUNT_COPY } from '../copy';
import { forgeName, statusWords } from '../forgeAccounts';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useForgeAccounts } from '../hooks/useForgeAccounts';
import {
  LINKED_ACCOUNTS_HASH,
  LINKED_ACCOUNTS_PATH,
} from '../personalAccounts';
import { ForgeMark } from './ForgeMark';

/**
 * Where the personal settings open (spec 239): who this person is here, the
 * forge accounts they have linked, and their session.
 *
 * Read-only about identity: the name and address come from the organization's
 * sign-in, so this page says where they change rather than offering a field
 * that could not save. The linked accounts are listed, not managed: linking
 * stays on Commits, where the forge's sign-in returns (spec 161), so there is
 * one place that links and this one points to it.
 */
export function AccountSettingsPage() {
  const { user, signOut, switchAccount } = useSession();
  const me = useCurrentUser();
  const { accounts } = useForgeAccounts();
  const isOrgAdmin = me.data?.is_org_admin ?? false;

  return (
    <div className="mx-auto flex w-full max-w-[44rem] flex-col gap-6 p-4 md:p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-foreground text-xl font-semibold">
          {ACCOUNT_COPY.title}
        </h1>
        <p className="text-muted-foreground text-sm">{ACCOUNT_COPY.intro}</p>
      </header>

      <SettingsPanel labelledBy="account-profile">
        <h2
          id="account-profile"
          className="text-foreground text-sm font-medium"
        >
          {ACCOUNT_COPY.profileHeading}
        </h2>
        {user ? (
          <div className="flex items-center gap-3">
            <UserAvatar
              id={user.id}
              displayName={user.displayName}
              email={user.email}
              className="size-10 text-sm"
            />
            <div className="flex min-w-0 flex-col">
              <span className="text-foreground truncate text-sm font-medium">
                {displayNameOf(user.displayName, user.email)}
              </span>
              <span className="text-muted-foreground truncate text-sm">
                {user.email}
              </span>
            </div>
          </div>
        ) : null}
        {/* The role only once it is known: "Member" flashing to "Organization
            admin" would say something false for a moment. */}
        {me.data ? (
          <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="text-foreground">
              {isOrgAdmin ? ACCOUNT_COPY.orgAdmin : ACCOUNT_COPY.member}
            </span>
            {isOrgAdmin ? (
              <>
                <span aria-hidden="true">·</span>
                <Link
                  to="/admin"
                  className="text-primary touch-pad underline-offset-4 hover:underline"
                >
                  {ACCOUNT_COPY.openConsole}
                </Link>
              </>
            ) : null}
          </p>
        ) : null}
        <p className="text-muted-foreground text-xs">
          {ACCOUNT_COPY.fromSignIn}
        </p>
      </SettingsPanel>

      <SettingsPanel labelledBy="account-linked">
        <h2 id="account-linked" className="text-foreground text-sm font-medium">
          {ACCOUNT_COPY.linkedHeading}
        </h2>
        {accounts.isPending ? (
          <p className="text-muted-foreground text-sm">
            {ACCOUNT_COPY.linkedLoading}
          </p>
        ) : accounts.isError ? (
          <p className="text-muted-foreground text-sm">
            {ACCOUNT_COPY.linkedFailed}
          </p>
        ) : accounts.data.items.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {ACCOUNT_COPY.linkedNone}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {accounts.data.items.map((account) => (
              <li key={account.id} className="flex items-center gap-3 text-sm">
                <ForgeMark provider={account.provider} className="size-5" />
                <span className="sr-only">{forgeName(account.provider)}: </span>
                <span className="text-foreground min-w-0 flex-1 truncate">
                  {ACCOUNT_COPY.linkedAs(account.forge_username, account.host)}
                </span>
                <span
                  className={
                    account.status === 'working'
                      ? 'text-muted-foreground'
                      : 'text-destructive'
                  }
                >
                  {statusWords(account)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Link
          to={LINKED_ACCOUNTS_PATH}
          hash={LINKED_ACCOUNTS_HASH}
          className="text-primary touch-pad self-start text-sm underline-offset-4 hover:underline"
        >
          {ACCOUNT_COPY.manageLinked}
        </Link>
      </SettingsPanel>

      <SettingsPanel labelledBy="account-sign-in">
        <h2
          id="account-sign-in"
          className="text-foreground text-sm font-medium"
        >
          {ACCOUNT_COPY.signInHeading}
        </h2>
        {user ? (
          <p className="text-muted-foreground text-sm">
            {ACCOUNT_COPY.signedInAs(user.email)}
          </p>
        ) : null}
        {/* As in the account menu: the next person to sign in here must not be
            offered this person's unsaved drafts (specs 144, 197). On a phone
            the two share the row's width (spec 275). */}
        <div className="flex flex-wrap gap-2 max-md:flex-nowrap max-md:[&>*]:flex-1">
          {switchAccount ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                clearPersonalDrafts();
                switchAccount();
              }}
            >
              {ACCOUNT_COPY.switchAccount}
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              clearPersonalDrafts();
              signOut();
            }}
          >
            {ACCOUNT_COPY.signOut}
          </Button>
        </div>
      </SettingsPanel>
    </div>
  );
}
